  /**
   * Forklift Inspection Component
   * ===============================
   * 
   * Individual form component 
   * 
   * Base Components:
   * - EXTENDS BaseFormComponent<ForkliftInspection> for form logic inheritance
   * - USES FormLayoutComponent for step navigation UI (in template)
   * 
   * Form Configuration:
   * - Form structure, steps, sections, and field organization are defined in the form model
   * - Signature and upload requirements are configured via form model metadata
   * - Component implements abstract methods: initializeForm() and getFormData()
   * - Component overrides setupDefaultStepValidation() for custom step validation rules
   * 
   * Key Functionality:
   * - Creates nested form groups for logical organization (enginePower, mechanicalSafety, signOff)
   * - Implements comprehensive equipment inspection checklist with Yes/No/N/A options
   * - Provides optional comment fields for each inspection item
   * - Implements form validation for safety compliance
   * - Implements step validation with signature requirements for final step
   * - Flattens nested form structure for backend submission
   * 
   * Form Model vs Component Responsibilities:
   * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
   * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
   * 
   * @author MacSafety Development Team
   * @version 1.0
   */

  import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { ForkliftInspection } from '../../../../../models/forms/forklift-inspection/forklift-inspection.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';

/**
 * Component for handling Daily Forklift Inspection submissions.
 * This form is used to document daily forklift inspections,
 * ensuring proper safety measures and equipment functionality.
 */ 
@Component({
  selector: 'app-forklift-inspection',
  templateUrl: './forklift-inspection.component.html',
  styleUrls: ['./forklift-inspection.component.css']
})
export class ForkliftInspectionComponent extends BaseFormComponent<ForkliftInspection> implements OnInit {
  // Form type identifier used for routing and form metadata lookup
  override formType = ForkliftInspection.getFormInfo().formType;
  
  // Instance of the form data model
  override model = new ForkliftInspection();
  
  // Define the form steps and their properties
  override steps: FormStep[] = ForkliftInspection.getFormSteps();

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(fb, formService, router, entraService, attachmentService, toastService, signatureService, screenSizeService);
    
    // Initialize form metadata first
    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    

  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Initializes the form with its controls and validators.
   * Sets up the form group with all required fields and their validation rules.
   */
  protected override initializeForm(): void {
    this.form = this.fb.group({
      enginePower: this.fb.group({
        date: [this.model.date, Validators.required],
        jobNameNumber: [this.model.jobNameNumber, Validators.required],
        battery: [this.model.battery, Validators.required],
        batteryComments: [this.model.batteryComments],
        engineOil: [this.model.engineOil, Validators.required],
        engineOilComments: [this.model.engineOilComments],
        coolant: [this.model.coolant, Validators.required],
        coolantComments: [this.model.coolantComments],
        hydraulicFluid: [this.model.hydraulicFluid, Validators.required],
        hydraulicFluidComments: [this.model.hydraulicFluidComments],
        hydraulicLines: [this.model.hydraulicLines, Validators.required],
        hydraulicLinesComments: [this.model.hydraulicLinesComments],
        transmissionFluid: [this.model.transmissionFluid, Validators.required],
        transmissionFluidComments: [this.model.transmissionFluidComments],
        propaneTank: [this.model.propaneTank, Validators.required],
        propaneTankComments: [this.model.propaneTankComments],
        propaneHose: [this.model.propaneHose, Validators.required],
        propaneHoseComments: [this.model.propaneHoseComments],
        horn: [this.model.horn, Validators.required],
        hornComments: [this.model.hornComments],
        lights: [this.model.lights, Validators.required],
        lightsComments: [this.model.lightsComments],
        reverseBeeper: [this.model.reverseBeeper, Validators.required],
        reverseBeeperComments: [this.model.reverseBeeperComments]
      }),
      mechanicalSafety: this.fb.group({
        forks: [this.model.forks, Validators.required],
        forksComments: [this.model.forksComments],
        carriage: [this.model.carriage, Validators.required],
        carriageComments: [this.model.carriageComments],
        liftChainsRollers: [this.model.liftChainsRollers, Validators.required],
        liftChainsRollersComments: [this.model.liftChainsRollersComments],
        raiseLower: [this.model.raiseLower, Validators.required],
        raiseLowerComments: [this.model.raiseLowerComments],
        tilt: [this.model.tilt, Validators.required],
        tiltComments: [this.model.tiltComments],
        sideShift: [this.model.sideShift, Validators.required],
        sideShiftComments: [this.model.sideShiftComments],
        steering: [this.model.steering, Validators.required],
        steeringComments: [this.model.steeringComments],
        brakes: [this.model.brakes, Validators.required],
        brakesComments: [this.model.brakesComments],
        tires: [this.model.tires, Validators.required],
        tiresComments: [this.model.tiresComments],
        mainBodyOfLiftTruck: [this.model.mainBodyOfLiftTruck, Validators.required],
        mainBodyOfLiftTruckComments: [this.model.mainBodyOfLiftTruckComments],
        overheadGuard: [this.model.overheadGuard, Validators.required],
        overheadGuardComments: [this.model.overheadGuardComments],
        boltsPins: [this.model.boltsPins, Validators.required],
        boltsPinsComments: [this.model.boltsPinsComments],
        boomIndicator: [this.model.boomIndicator, Validators.required],
        boomIndicatorComments: [this.model.boomIndicatorComments],
        glassMirrors: [this.model.glassMirrors, Validators.required],
        glassMirrorsComments: [this.model.glassMirrorsComments],
        seatBelts: [this.model.seatBelts, Validators.required],
        seatBeltsComments: [this.model.seatBeltsComments],
        fireExtinguisher: [this.model.fireExtinguisher, Validators.required],
        fireExtinguisherComments: [this.model.fireExtinguisherComments],
        warningDecals: [this.model.warningDecals, Validators.required],
        warningDecalsComments: [this.model.warningDecalsComments],
        riggingShackles: [this.model.riggingShackles, Validators.required],
        riggingShacklesComments: [this.model.riggingShacklesComments],
        lubricantFittings: [this.model.lubricantFittings, Validators.required],
        lubricantFittingsComments: [this.model.lubricantFittingsComments]
      }),
      signOff: this.fb.group({
        operatorName: [this.model.operatorName, Validators.required],
        generalComments: [this.model.generalComments]
      })
    });
  }

  // Form group getters
  get enginePowerFormGroup(): FormGroup {
    return this.form.get('enginePower') as FormGroup;
  }

  get mechanicalSafetyFormGroup(): FormGroup {
    return this.form.get('mechanicalSafety') as FormGroup;
  }

  get signOffFormGroup(): FormGroup {
    return this.form.get('signOff') as FormGroup;
  }

  /**
   * Retrieves the current form data and creates a new ForkliftInspection instance.
   * This method is called when submitting the form.
   */
  protected override getFormData(): ForkliftInspection {
    const formValue = this.form.value;
    
    // Flatten the nested form groups into a single object
    const flattenedData = {
      // Engine & Power Systems (includes basic info)
      ...formValue.enginePower,
      
      // Mechanical & Safety Systems
      ...formValue.mechanicalSafety,
      
      // Sign-off info
      ...formValue.signOff
    };
    
    const forkliftInspection = new ForkliftInspection(flattenedData);
    const updatedForkliftInspection = this.updateModelWithAttachments(forkliftInspection);
    return updatedForkliftInspection;
  }

  /**
   * Sets up validation rules for each step of the forklift inspection form.
   * Each step has specific validation requirements that must be met before proceeding:
   * - Engine & Power: Requires date, job name/number, and all engine/power system checks
   * - Mechanical & Safety: Requires all mechanical and safety system checks
   * - Sign-off: Requires operator name and signature
   * 
   * The validation ensures that:
   * 1. All required fields are filled out
   * 2. Required signatures are collected
   * 3. Form data meets safety and compliance requirements
   */
   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Define step indices
    const SIGNATURE_REQUIREMENT_INDEX: number = 0;

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('engine-power', this.enginePowerFormGroup);
    this.setupStepValidation('mechanical-safety', this.mechanicalSafetyFormGroup);
    this.setupStepValidation('signOff', this.signOffFormGroup, {signatureIndex: [SIGNATURE_REQUIREMENT_INDEX]});
  }
}